/**
 * The pure half of chat identity resolution: how a handle becomes a name.
 *
 * Separate from the hooks so it can be tested without them — and it must stay
 * free of `backoffice-api`, whose graph reaches a bonzai file this vitest
 * cannot transform.
 */
import type { PeerProfile } from "../types"
import { jidLocalPart, type Jid } from "../xmpp/jid"

/**
 * The display names for the three states resolution can't name a peer from the
 * directory. Translated by the caller; English here so the pure module and its
 * tests stay hook-free.
 *
 * - `resolving`: either stage still in flight — an ellipsis, not a per-row skeleton
 * - `failed`:    either stage failed — the reader is told once, by toast; this fills the slot
 * - `someone`:   a handle with no platform mapping left — erased or offboarded
 */
export type ResolveLabels = {
  resolving: string
  failed: string
  someone: string
}

const DEFAULT_LABELS: ResolveLabels = {
  resolving: "…",
  failed: "Unknown",
  someone: "Someone",
}

/** The fields resolution needs off a `getChatUsers` row. */
export type DirectoryUser = {
  id: string
  display_name: string | null
  avatar: string
}

/**
 * Every handle to resolve in one batch: the peers' own, plus any already-bare
 * handles (a group's members). Unique and sorted, so the query key is stable.
 */
export function peerHandles(peers: Jid[], extra: string[] = []): string[] {
  return [...new Set([...peers.map(jidLocalPart), ...extra])].sort()
}

/** The distinct user ids stage 1 resolved to, sorted for a stable query key. */
export function userIdsOf(
  userIdByHandle: Map<string, string> | undefined
): string[] {
  return userIdByHandle ? [...new Set(userIdByHandle.values())].sort() : []
}

/** The directory's rows, keyed by user id. */
export function buildProfileMap(
  users: DirectoryUser[]
): Map<string, PeerProfile> {
  return new Map(
    users.map((user) => [
      user.id,
      {
        displayName: user.display_name ?? user.id,
        avatar: user.avatar || null,
      },
    ])
  )
}

/**
 * One handle's profile — the whole decision table over both stages.
 *
 * Keyed on the handle rather than the JID because group members arrive as bare
 * handles from `getGroup`, with no JID around them.
 *
 * `seeded` is a profile we already knew when we opened this chat — a DM peer
 * the picker just handed us (FAME-713). It fills in wherever the two stages
 * have no real answer yet, so a just-started DM shows a name instead of a
 * placeholder while `resolveHandles` catches up. The directory always wins
 * once it answers, so the seed can never mask a real profile.
 *
 * `failed` means a stage that has no answer will not get one: its slot reads
 * as failed rather than loading. `labels` carries the translated placeholder
 * names; see `ResolveLabels`.
 */
export function resolveProfile(
  userIdByHandle: Map<string, string> | undefined,
  profiles: Map<string, PeerProfile> | undefined,
  handle: string,
  seeded?: Map<string, PeerProfile>,
  failed = false,
  labels: ResolveLabels = DEFAULT_LABELS
): PeerProfile {
  const seed = seeded?.get(handle)
  const pending: PeerProfile = {
    displayName: failed ? labels.failed : labels.resolving,
    avatar: null,
  }

  if (userIdByHandle === undefined) return seed ?? pending

  const userId = userIdByHandle.get(handle)
  if (userId === undefined)
    return seed ?? { displayName: labels.someone, avatar: null }

  if (profiles === undefined) return seed ?? pending

  // Normally a hit. The fallbacks are a directory that hid them from us: the
  // seed if we have one, else the bare user id as the best name left.
  return profiles.get(userId) ?? seed ?? { displayName: userId, avatar: null }
}
